import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { api, impersonation, tokens } from '../lib/api';
import { Socket, type Msg } from '../lib/socket';
import { applyPalette, legacyModeTheme } from '../lib/palette';
import { disablePushSubscription, syncPushSubscription } from '../lib/push';
import type { Child, DeviceStatus, NotifyPrefs, Parent, Policy, UpdateStatus } from '../lib/types';

export interface Toast {
  id: number;
  text: string;
  tone: 'alert' | 'info' | 'good';
  action?: { label: string; run: () => void };
}

interface ParentState {
  parent: Parent;
  setParent: (p: Parent) => void;
  children: Child[];
  loaded: boolean;
  connected: boolean;
  socket: Socket | null;
  /** The newest agent version this server can install, or null if none is bundled. */
  agentLatest: string | null;
  /** The newest Mac app version this server offers (#293). */
  agentMacLatest: string | null;
  extensionLatest: string | null;
  /** In-flight self-updates, keyed by device id. */
  updates: Record<string, UpdateStatus>;
  refresh: () => Promise<void>;
  replaceChild: (c: Child) => void;
  toasts: Toast[];
  toast: (t: Omit<Toast, 'id'>) => void;
  dismiss: (id: number) => void;
  signOut: () => void;
}

const Ctx = createContext<ParentState | null>(null);

export function useParent() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useParent outside ParentProvider');
  return ctx;
}

function notify(title: string, body: string, enabled: boolean) {
  if (!enabled) return; // the parent turned this category's on-device notifications off (#213)
  try {
    if ('Notification' in window && Notification.permission === 'granted' && document.visibilityState !== 'visible') {
      new Notification(title, { body, icon: '/icon.svg', tag: body });
    }
    navigator.vibrate?.(200);
  } catch {
    /* notifications are a bonus */
  }
}

export function ParentProvider({ initial, onSignOut, children: content }: { initial: Parent; onSignOut: () => void; children: ReactNode }) {
  const [parent, setParent] = useState(initial);
  const [kids, setKids] = useState<Child[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [connected, setConnected] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [agentLatest, setAgentLatest] = useState<string | null>(null);
  const [agentMacLatest, setAgentMacLatest] = useState<string | null>(null);
  const [extensionLatest, setExtensionLatest] = useState<string | null>(null);
  const [updates, setUpdates] = useState<Record<string, UpdateStatus>>({});
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastId = useRef(0);
  const kidsRef = useRef(kids);
  kidsRef.current = kids;
  // Current on-device notification prefs, read inside socket handlers (#213). Defaults: all browser
  // categories on, matching the server's defaultBrowserPrefs, until the parent changes them.
  const browserPrefsRef = useRef<NotifyPrefs | undefined>(undefined);
  browserPrefsRef.current = parent.browserPrefs ?? { focus: true, offline: true, done: true, help: true, chat: true, whiteboard: true, support: true, behind: true, deadlines: true, evaluator: true };
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const toast = useCallback(
    (t: Omit<Toast, 'id'>) => {
      const id = ++toastId.current;
      setToasts((list) => [...list.slice(-3), { ...t, id }]);
      window.setTimeout(() => dismiss(id), t.action ? 20_000 : 8_000);
    },
    [dismiss],
  );

  const refresh = useCallback(async () => {
    const list = await api.get<Child[]>('/api/children');
    setKids(list);
    setLoaded(true);
  }, []);

  const replaceChild = useCallback((c: Child) => setKids((list) => list.map((k) => (k.id === c.id ? c : k))), []);

  const signOut = useCallback(() => {
    // While an admin is viewing as a parent, "sign out" ends that and goes back to the admin portal.
    if (impersonation.active()) {
      impersonation.end().then(() => location.assign('/admin'));
      return;
    }
    api.post('/api/auth/logout').catch(() => undefined);
    // Stop this device's push notifications too, so whoever uses it next doesn't get this parent's alerts (#480). The
    // browser unsubscribe is what counts: the server drops the subscription the next time the push service says it's gone.
    void disablePushSubscription();
    tokens.setParent(null);
    onSignOut();
  }, [onSignOut]);

  useEffect(() => {
    refresh().catch(() => setLoaded(true));
  }, [refresh]);

  useEffect(() => {
    const token = tokens.parent();
    if (!token) return;
    const s = new Socket(token, 'parent');
    setSocket(s);
    const childName = (id: unknown) => kidsRef.current.find((k) => k.id === id)?.name ?? 'Your child';

    const setDevice = (deviceId: string, online: boolean, status: DeviceStatus) =>
      setKids((list) =>
        list.map((k) => ({ ...k, devices: k.devices.map((d) => (d.id === deviceId ? { ...d, online, status, lastSeenAt: Date.now() } : d)) })),
      );

    const offs = [
      s.on('connection', (m) => {
        setConnected(!!m.connected);
        if (m.connected) refresh().catch(() => undefined);
      }),
      s.on('hello', (m) => {
        setAgentLatest((m.agentLatest as string | null) ?? null);
        setAgentMacLatest((m.agentMacLatest as string | null) ?? null);
        setExtensionLatest((m.extensionLatest as string | null) ?? null);
        for (const o of (m.online as { deviceId: string; status: DeviceStatus }[]) ?? []) setDevice(o.deviceId, true, o.status);
      }),
      s.on('presence', (m) => {
        // A newly paired device, or a shared computer that another child picked up (#442): reload the list.
        const known = kidsRef.current.some((k) => k.id === m.childId && k.devices.some((d) => d.id === m.deviceId));
        if (!known) refresh().catch(() => undefined);
        else setDevice(m.deviceId as string, !!m.online, m.status as DeviceStatus);
      }),
      s.on('policy', (m) => setKids((list) => list.map((k) => (k.id === m.childId ? { ...k, policy: m.policy as Policy, mode: (m.policy as Policy).mode } : k)))),
      s.on('device-version', (m) => {
        setKids((list) =>
          list.map((k) => ({ ...k, devices: k.devices.map((d) => (d.id === m.deviceId ? { ...d, appVersion: m.appVersion as string } : d)) })),
        );
        // A fresh version report means the update finished: clear any progress for it.
        setUpdates((u) => {
          const next = { ...u };
          delete next[m.deviceId as string];
          return next;
        });
      }),
      s.on('update-status', (m) => {
        const deviceId = m.deviceId as string;
        const status: UpdateStatus = {
          phase: m.phase as UpdateStatus['phase'],
          percent: Number(m.percent) || 0,
          version: (m.version as string) || undefined,
          message: (m.message as string) || undefined,
        };
        setUpdates((u) => ({ ...u, [deviceId]: status }));
        // "Up to date" and errors are momentary: show briefly, then clear.
        if (status.phase === 'uptodate' || status.phase === 'error') {
          window.setTimeout(
            () =>
              setUpdates((u) => {
                if (u[deviceId]?.phase !== status.phase) return u; // a newer status arrived
                const next = { ...u };
                delete next[deviceId];
                return next;
              }),
            6000,
          );
        }
      }),
      s.on('alert', (m) => {
        toast({ text: String(m.text), tone: 'alert' });
        // The offline alert arrives on the same 'alert' event (tagged event:'offline'); it belongs to the
        // offline category, not focus (#217).
        const offline = m.event === 'offline';
        notify(
          offline ? 'ClassGuardian offline' : 'Focus alert',
          String(m.text),
          !!(offline ? browserPrefsRef.current?.offline : browserPrefsRef.current?.focus),
        );
      }),
      // The child opened the whiteboard from School Resources on their computer (#267): offer to join them.
      s.on('whiteboard', (m: Msg) => {
        if (m.open !== true || !m.deviceId) return; // strokes/clears are handled by the Whiteboard tab
        toast({
          text: `${childName(m.childId)} opened the whiteboard.`,
          tone: 'info',
          action: { label: 'Open', run: () => navigateRef.current(`/kids/${m.childId}/whiteboard`) },
        });
        notify('Whiteboard', `${childName(m.childId)} opened the whiteboard.`, !!browserPrefsRef.current?.whiteboard);
      }),
      // A chat message from the child (#273): a notice unless that chat is already open on screen.
      s.on('chat', (m: Msg) => {
        if (m.from !== 'child') return;
        const name = childName(m.childId);
        notify(`Message from ${name}`, String(m.text ?? ''), !!browserPrefsRef.current?.chat);
        if (window.location.pathname === `/kids/${m.childId}/chat`) return;
        toast({ text: `${name}: “${String(m.text ?? '')}”`, tone: 'info', action: { label: 'Reply', run: () => navigateRef.current(`/kids/${m.childId}/chat`) } });
      }),
      // The child pressed "Ask for help" on their computer or in the Student Portal (#232).
      // The child says they're done for the day (#440): offer to end school time until tomorrow.
      s.on('done-request', (m: Msg) => {
        const text = `${childName(m.childId)} says they’re done for today.`;
        toast({
          text,
          tone: 'info',
          action: {
            label: 'End school for today',
            run: () =>
              api
                .post<Child>(`/api/children/${m.childId}/done-for-day`, { approve: true })
                .then(replaceChild)
                .catch((e) => toast({ text: e.message, tone: 'alert' })),
          },
        });
        notify('Done for today', text, !!browserPrefsRef.current?.done);
      }),
      s.on('help-request', (m: Msg) => {
        const note = String(m.note ?? '');
        const text = `${childName(m.childId)} needs help${note ? `: “${note}”` : '.'}`;
        toast({ text, tone: 'alert', action: { label: 'Chat', run: () => navigateRef.current(`/kids/${m.childId}/chat`) } });
        notify('Help request', text, !!browserPrefsRef.current?.help);
      }),
      // The student turned in an assignment from the Student Portal (#188).
      s.on('planner-submit', (m) => toast({ text: `${String(m.name)} turned in “${String(m.title)}”.`, tone: 'good' })),
      s.on('signed-out', () => signOut()),
    ];
    return () => {
      offs.forEach((off) => off());
      s.close();
      setSocket(null);
    };
  }, [refresh, toast, replaceChild, signOut]);

  // The parent's color theme (#251), from their account, so it follows them to every device.
  useEffect(() => applyPalette(parent.theme), [parent.theme]);
  // Light or dark used to be a separate per-browser switch; move an old choice into the theme once (#309).
  useEffect(() => {
    const moved = legacyModeTheme(parent.theme ?? '');
    if (moved) api.patch<Parent>('/api/me', { theme: moved }).then(setParent).catch(() => undefined);
    // Only on sign-in: later theme changes come from the picker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent.id]);

  // Keep this device's Web Push subscription registered so notifications arrive when the app is closed
  // (#222). Only subscribes once permission is granted; no-ops otherwise.
  useEffect(() => {
    void syncPushSubscription();
  }, []);

  // Breaks and schedules change on their own: re-render every 30 s so labels stay current.
  const [, tick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const value = useMemo<ParentState>(
    () => ({ parent, setParent, children: kids, loaded, connected, socket, agentLatest, agentMacLatest, extensionLatest, updates, refresh, replaceChild, toasts, toast, dismiss, signOut }),
    [parent, kids, loaded, connected, socket, agentLatest, agentMacLatest, extensionLatest, updates, refresh, replaceChild, toasts, toast, dismiss, signOut],
  );
  return <Ctx.Provider value={value}>{content}</Ctx.Provider>;
}
